import { animateChild, query, transition, trigger } from '@angular/animations';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { AmplitudeService } from '@inyova/inyova-shared';
import { FeatureFlags } from '@inyova/models';

import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import * as StrategyActions from '@strategy/strategy.actions';
import * as fromAppRoot from '@app/app.reducers';
import * as fromStrategy from '@strategy/reducers';

import { ThirdPartyServiceItem } from '@shared/models/ThirdPartService';
import { TrackingService } from '@shared/services/tracking.service';

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-strategy',
  styleUrls: ['./strategy.component.scss'],
  templateUrl: './strategy.component.html',
  animations: [trigger('ngIfAnimation', [transition(':enter, :leave', [query('@containerFade', animateChild())])])],
  standalone: false
})
export class StrategyComponent implements OnDestroy, OnInit {
  accountId: null | string = null;
  canShowInterviewWidget: boolean;
  changeStrategyDisabled = false;
  editMode = true;
  generateProfileLoader$: Observable<boolean>;
  highestTabIndex = 1;
  isInitialised = false;
  isEve2 = false;
  isEditModeEve2$: Observable<boolean>;
  numberOfCompanies$: Observable<number>;
  portfolioUndoLoading$: Observable<boolean>;
  productType: string;
  qlOnboardingStep: number;
  showGeneratingSpinner = false;
  showInterviewNotification: boolean;
  showInterviewWidget: boolean;
  showPortfolioWarningModal$: Observable<boolean>;
  flipperBrokenPf$: Observable<ThirdPartyServiceItem>;
  stepStatus: number;
  strategyWasChanged: { modalOpened: boolean; redirectUrl: string; strategyWasChanged: boolean };
  tabSelected: number;
  treesModalOpened = false;
  showEve2Modal = false;

  protected readonly onDestroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private trackingService: TrackingService,
    private amplitudeService: AmplitudeService,
    private router: Router
  ) {}

  ngOnInit() {
    combineLatest([
      this.store.select(fromAppRoot.selectAccountFeatureFlag(FeatureFlags.EVE2)),
      this.store.select(fromAppRoot.selectCustomerAndCurrentInvestAccount)
    ])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(([isEve2, { customer, currentAccountDetail }]) => {
        if (!customer || !customer.email || !currentAccountDetail?.onboarding_step) {
          return;
        }

        this.isInitialised = true;
        this.editMode = currentAccountDetail.edit_mode;
        this.stepStatus = currentAccountDetail.step_status;
        this.productType = currentAccountDetail.kind;
        this.showInterviewNotification = customer.interview_widget;
        this.qlOnboardingStep = this.stepStatus >= 2 ? 5 : currentAccountDetail.ql_onboarding_step;
        this.isEve2 = isEve2;
        this.showEve2Modal = currentAccountDetail.show_eve2_explanation_modal;

        this.trackPageViewEvent();

        if (this.stepStatus <= 2 && document.cookie.indexOf('seenReferral') === -1) {
          this.treesModalOpened = customer.is_friend;
        }

        if (!this.accountId) {
          this.accountId = currentAccountDetail.id;
        }

        if (!isEve2) {
          this.store.dispatch(StrategyActions.setTabIndex({ tabIndex: this.qlOnboardingStep }));
        }
      });

    this.store.pipe(select(fromStrategy.selectGenerateStrategyLoader), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.showGeneratingSpinner = value;
    });
    this.isEditModeEve2$ = this.store.select(fromStrategy.selectIsEditModeEve2);
    this.generateProfileLoader$ = this.store.select(fromStrategy.selectGenerateProfileLoader);
    this.numberOfCompanies$ = this.store.select(fromStrategy.selectPortfolioCompanyLength);
    this.showPortfolioWarningModal$ = this.store.select(fromStrategy.selectPortfolioWarningModal);
    this.portfolioUndoLoading$ = this.store.select(fromStrategy.selectPortfolioUndoLoading);
    // TODO: Remove this after releasing broken portfolio dialog
    this.flipperBrokenPf$ = this.store.select(fromAppRoot.selectThirdPartyServices('broken_pf')) as Observable<ThirdPartyServiceItem>;
    this.store
      .pipe(select(fromStrategy.selectStrategyChanges), takeUntil(this.onDestroy$))
      .subscribe(({ changeStrategyDisabled, strategyWasChanged }) => {
        this.changeStrategyDisabled = changeStrategyDisabled;
        this.strategyWasChanged = strategyWasChanged;
      });

    this.store.pipe(select(fromStrategy.selectInterviewWidget), takeUntil(this.onDestroy$)).subscribe((canShowInterviewWidget: boolean) => {
      this.canShowInterviewWidget = canShowInterviewWidget;
    });

    this.store.pipe(select(fromStrategy.selectTabIndex), debounceTime(0), takeUntil(this.onDestroy$)).subscribe(({ accountId, tabIndex }) => {
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
      this.tabSelected = tabIndex;

      if (this.accountId !== accountId) {
        this.accountId = accountId;
        this.highestTabIndex = 1;
      }

      this.highestTabIndex = tabIndex > this.highestTabIndex ? tabIndex : this.highestTabIndex;
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  showInterviewModal($event: boolean): void {
    this.showInterviewNotification = false;
    this.showInterviewWidget = $event;
  }

  hideInterviewModal(): void {
    this.showInterviewWidget = false;
  }

  onCloseLoader(buttonEvent: boolean) {
    this.trackingService.trackActivity(`${buttonEvent ? '[Button]' : '[Timeout]'} Portfolio generator loader: Closed`);
    this.store.dispatch(StrategyActions.closeGeneratePortfolioLoader());
  }

  onDismissEve2Modal({ redirect }) {
    this.trackingService.trackActivity(`[Button] Eve2 explanation - ${redirect ? 'review strategy' : ' continue onboarding'}`);
    this.showEve2Modal = false;
    if (redirect) {
      this.router.navigate(['/strategy']);
    }
  }

  onCloseWarning() {
    this.trackingService.trackActivity(`[Button] Broken portfolio modal: Closed`);
    this.store.dispatch(StrategyActions.hideBrokenPortfolioWarning());
  }

  onUndoChange() {
    this.trackingService.trackActivity(`[Button] Broken portfolio modal: Undo`);
    this.store.dispatch(StrategyActions.undoStrategyActions('dialog'));
  }

  trackPageViewEvent() {
    this.amplitudeService.trackEvent('Strategy Viewed', {
      offering_account_type: this.productType,
      placement: this.stepStatus < 5 ? 'onboarding' : 'strategy_configuration'
    });
  }
}
